/**
 * Landing Page with Supabase Auth Login
 *
 * Public landing page with Supabase-based authentication.
 * Uses dark theme styling consistent with the app's dark mode.
 *
 * After a password the card shows the authenticator step (set one up, or
 * enter its code) until the session is fully verified; a passkey sign-in
 * needs no code. Either way it then redirects to the dashboard. "Forgot
 * password?" swaps the sign-in form for the reset-email form. A reset-link
 * session that lands here (Supabase falls back to the site root when the
 * reset URL is not on its redirect allow list) goes on to /reset-password.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@shared/auth/AuthContext';
import type { MfaStep } from '@shared/auth/mfa';
import { AuthCardLayout } from '../components/AuthCardLayout';
import { ForgotPasswordForm } from '../components/ForgotPasswordForm';
import { SignInForm } from '../components/SignInForm';
import { TotpChallengeStep } from '../components/TotpChallengeStep';
import { TotpEnrollStep } from '../components/TotpEnrollStep';

function SecondFactorStep({ step }: { step: MfaStep }) {
  return step === 'enroll' ? <TotpEnrollStep /> : <TotpChallengeStep />;
}

function FirstFactorStep() {
  /** The email handed to the reset form; null while signing in. */
  const [resetEmail, setResetEmail] = useState<string | null>(null);

  return resetEmail === null ? (
    <SignInForm onForgotPassword={setResetEmail} />
  ) : (
    <ForgotPasswordForm initialEmail={resetEmail} onBack={() => setResetEmail(null)} />
  );
}

export function LandingPage() {
  const { isAuthenticated, isLoading, mfaStep, isPasswordResetSession } = useAuth();

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return <AuthCardLayout />;
  }

  if (mfaStep && isPasswordResetSession) {
    return <Navigate to="/reset-password" replace />;
  }

  return (
    <AuthCardLayout>
      {mfaStep ? <SecondFactorStep step={mfaStep} /> : <FirstFactorStep />}
    </AuthCardLayout>
  );
}

export default LandingPage;
